import { ApiCall } from "components/common/services/ApiServices";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import { t } from "components/CentralDataMangement/translation/Translation";
import { useSelector } from "react-redux/es/hooks/useSelector";
import {
  CENTRAL_DATA_MANAGEMENT_MICROSERVICE,
  OFFICE_MANAGER,
  RECRUITER,
} from "Constants";
import { Link } from "react-router-dom";
import {
  createQueryParams,
  filterAndMerge,
  getQueryParamsFromRedux,
} from "components/common/utlis/HelperFunctions";
import { createFilterConfig } from "./Filters";
import { useDispatch } from "react-redux";
import { RootState } from "store";
import {
  clearDestinationPath,
  setDestinationPath,
} from "features/NavigationSlice";
import SearchFields from "components/common/atoms/SearchFields";
import ResetBtn from "components/common/atoms/ResetBtn";
import AdvancedSearchBtn from "components/common/atoms/AdvancedSearchBtn";
import Title from "components/common/atoms/Title";
import AdvancedSearch from "components/common/molecules/AdvancedSearch";
import NavBar from "components/common/atoms/NavBar";
import {
  scrappingData,
  intialDropDownList,
  intialscrappingData,
} from "./Interface";
import RightArrow from "static/images/RightArrow";
import PaginationNew from "components/common/atoms/PaginationNew";
import Sorting from "components/common/atoms/Sorting";
import SpinnerWrapper from "components/common/atoms/SpinnerWrapper";
import TextEllipsis from "components/common/atoms/TextEllipsis";
import NoRecords from "components/common/atoms/NoRecords";
import { selectAuth } from "features/auth/AuthSlice";
import LinkTo from "components/common/atoms/LinkTo";
import { useState, useEffect, useRef, FormEvent, ChangeEvent } from "react";
import CustomNotify from "components/common/atoms/CustomNotify";
import LoadingIcon from "components/common/utlis/LoadingIcon";
import ExportFile from "components/common/atoms/ExportFile";
import { ClaritalkCallIcon } from "components/CentralDataMangement/claritalk";

interface ScrapingApplication {
  main: {
    id: string | number;
    posting_date?: string;
    posted_date?: string;
    status?: number;
    created_at?: string;
  };
  basic?: {
    position?: string;
    company?: string;
    telephone?: string;
    gsm?: string;
    source?: string;
    vat?: string;
    status?: number;
  };
  address?: {
    city1?: string;
    post_code: string;
  };
  contacts?: Array<{
    fName?: string;
    lName?: string;
    email?: string;
    role?: string;
  }>;
  vacancies?: Array<{
    id: number;
    position: string;
    vacancy_status: number;
  }>;
}

interface PaginationData {
  totalPages: number;
  totalRecords: number;
}

interface ActiveTab {
  tabFieldName: string;
  tabFieldValue: number;
}

interface NavItem {
  label: string;
  fieldName: string;
  value: number;
}

type SortType = "" | "asc" | "desc";

// Secure Storage Utility Functions
const SecureStorage = {
  setItem: (key: string, value: any) => {
    try {
      const encryptedValue = btoa(JSON.stringify(value));
      localStorage.setItem(key, encryptedValue);
    } catch (error) {
      console.error("Error storing data securely:", error);
    }
  },

  getItem: (key: string) => {
    try {
      const encryptedValue = localStorage.getItem(key);
      if (!encryptedValue) return null;
      return JSON.parse(atob(encryptedValue));
    } catch (error) {
      console.error("Error retrieving data securely:", error);
      return null;
    }
  },

  removeItem: (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error("Error removing data securely:", error);
    }
  },
};

const ManageScraping = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const authUser = useSelector(selectAuth);
  const [dropdownList, setDropdownList] = useState(intialDropDownList);
  // CR: dropdown options are fetched once (via loadDropdown) and reused on later searches.
  const dropdownLoadedRef = useRef(false);
  const dispatch = useDispatch();
  const [ScrapingApplication, setScrapingApplication] = useState<
    ScrapingApplication[]
  >([]);
  const [paginationData, setPaginationData] = useState<PaginationData>({
    totalPages: 1,
    totalRecords: 1,
  });

  // Import status tracking state
  const [importStatus, setImportStatus] = useState<{
    isImporting: boolean;
    fileName: string;
    status: "importing" | "completed" | "failed" | null;
  }>({
    isImporting: false,
    fileName: "",
    status: null,
  });
  const destinationPath = useSelector(
    (state: RootState) => state.navigation.destinationPath
  );
  const queryParams = getQueryParamsFromRedux(destinationPath);

  const filters = createFilterConfig(dropdownList);

  // Helper function to safely convert date strings to Date objects
  const safeParseDate = (dateInput: any): Date | null => {
    if (!dateInput) return null;

    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }

    if (typeof dateInput === "string" && dateInput.trim() !== "") {
      const parsedDate = new Date(dateInput);
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    return null;
  };

  // Initialize search data with proper Date objects
  const defaultSearchData: scrappingData = {
    ...intialscrappingData,
    office:
      authUser?.role === RECRUITER || authUser?.role === OFFICE_MANAGER
        ? [authUser?.officeId, authUser?.correspondingOffice?.id]
        : [],
    currentPage: 1,
    recordsPerPage: 10,
    orderByColumn: "created_at",
    orderIn: "desc" as SortType,
    fromDate: null,
    toDate: null,
    posted_date: null,
    post_code: // CR: multi-select -> array; keep the recruiter/office-manager default as one preselected code
      (authUser?.role === RECRUITER || authUser?.role === OFFICE_MANAGER) &&
      authUser?.officePostalCode
        ? [Number(authUser.officePostalCode)]
        : [],
  };

  // advert keyword: advanced-search fields for the scraper. advert_keyword searches
  // inside the scraped advert text (job description) on the backend.
  const defaultAdvSearchData: Record<string, any> = { advert_keyword: "" };

  // Process query params to ensure dates are proper Date objects
  const processedQueryParams = { ...queryParams };
  if (processedQueryParams.fromDate) {
    processedQueryParams.fromDate = safeParseDate(
      processedQueryParams.fromDate
    );
  }
  if (processedQueryParams.toDate) {
    processedQueryParams.toDate = safeParseDate(processedQueryParams.toDate);
  }
  if (processedQueryParams.posted_date) {
    processedQueryParams.posted_date = safeParseDate(
      processedQueryParams.posted_date
    );
  }
  // CR: post_code is now a multi-select (array). Coerce any legacy scalar coming from an
  // older saved/shared search URL into an array, so the MultiSelect never receives a string
  // (PrimeReact calls value.some(...) internally and crashes on a non-array value).
  if (processedQueryParams.post_code !== undefined) {
    processedQueryParams.post_code = Array.isArray(processedQueryParams.post_code)
      ? processedQueryParams.post_code
      : processedQueryParams.post_code === null ||
        processedQueryParams.post_code === ""
      ? []
      : [processedQueryParams.post_code];
  }

  const filteredSearchData = filterAndMerge(
    defaultSearchData,
    processedQueryParams
  );
  const filteredAdvSearchData = filterAndMerge(
    defaultAdvSearchData,
    processedQueryParams
  );

  const defaultTabData: ActiveTab = {
    tabFieldName: "type",
    tabFieldValue: 3,
  };
  const filteredTabData = filterAndMerge(defaultTabData, processedQueryParams);
  const [activeTab, setActiveTab] = useState<ActiveTab>({
    ...defaultTabData,
    ...filteredTabData,
  });

  const [searchData, setSearchData] = useState<scrappingData>({
    ...defaultSearchData,
    ...filteredSearchData,
  });

  const [advSearchData, setAdvSearchData] = useState<Record<string, any>>({
    ...defaultAdvSearchData,
    ...filteredAdvSearchData,
  });

  const navItems: NavItem[] = [
    { label: "All", fieldName: "type", value: 3 },
    { label: "New", fieldName: "type", value: 1 },
    { label: "Completed", fieldName: "type", value: 2 },
  ];

  const [searchModal, setSearchModal] = useState<boolean>(false);

  // Check import status on component mount and periodically
  useEffect(() => {
    checkImportStatus();

    // Set up periodic checking for import status
    const statusCheckInterval = setInterval(checkImportStatus, 5000); // Check every 5 seconds

    return () => clearInterval(statusCheckInterval);
  }, []);

  const checkImportStatus = () => {
    const importData = SecureStorage.getItem("scraping_import_status");

    if (importData) {
      const { status, fileName, response, error, completedTime } = importData;

      if (status === "importing") {
        setImportStatus({
          isImporting: true,
          fileName: fileName || "",
          status: "importing",
        });
      } else if (status === "completed") {
        // Show success message and clear storage
        if (response && response.status === 200) {
          CustomNotify({
            type: "success",
            message: t("Data scraped successfully!"),
          });
        }

        setImportStatus({
          isImporting: false,
          fileName: "",
          status: "completed",
        });

        // Clear the import status from secure storage
        SecureStorage.removeItem("scraping_import_status");

        // Refresh the data
        const filterData = {
          ...searchData,
          ...advSearchData,
          type: activeTab.tabFieldValue,
        };
        fetchDetails(filterData);
      } else if (status === "failed") {
        // Show error message and clear storage
        CustomNotify({
          type: "error",
          message: t(error || "Data import failed. Please try again."),
        });

        setImportStatus({
          isImporting: false,
          fileName: "",
          status: "failed",
        });

        // Clear the import status from secure storage
        SecureStorage.removeItem("scraping_import_status");
      }
    } else {
      // No import in progress
      setImportStatus({
        isImporting: false,
        fileName: "",
        status: null,
      });
    }
  };

  useEffect(() => {
    const filterData = {
      ...searchData,
      ...advSearchData,
      type: activeTab.tabFieldValue,
    };

    const searchUrl = createQueryParams({ ...filterData, ...activeTab });
    dispatch(setDestinationPath(searchUrl));

    const delayDebounceFn = setTimeout(() => {
      fetchDetails(filterData);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchData, advSearchData, activeTab]);

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "";

    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      return dateString;
    }

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return dateString;
      }

      // Format as DD-MM-YYYY to match the PHP service format
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      console.warn("Error formatting date:", dateString, error);
      return dateString || "";
    }
  };

  // const formatDateForAPI = (date: Date | string | null | undefined): string => {
  //   if (!date) return "";
  //   console.log(date, "date");

  //   if (date instanceof Date) {
  //     return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
  //   }

  //   if (typeof date === "string" && date.trim() !== "") {
  //     const parsedDate = new Date(date);
  //     if (!isNaN(parsedDate.getTime())) {
  //       return parsedDate.toISOString().split("T")[0];
  //     }
  //   }

  //   return "";
  // };

  const formatDateForAPI = (date: Date | string | null | undefined): string => {
    if (!date) return "";

    let d: Date;

    if (date instanceof Date) {
      d = date;
    } else if (typeof date === "string" && date.trim() !== "") {
      d = new Date(date);
      if (isNaN(d.getTime())) return "";
    } else {
      return "";
    }

    // Use local year, month, day
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0"); // months are 0-indexed
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };


  const fetchDetails = async (filterData: Record<string, any>) => {
    setLoading(true);
    try {
      const formattedFilterData = { ...filterData };

      if (formattedFilterData.posted_date) {
        formattedFilterData.posted_date = formatDateForAPI(
          formattedFilterData.posted_date
        );
      }

      formattedFilterData.fromDate = formatDateForAPI(
        formattedFilterData.fromDate
      );
      formattedFilterData.toDate = formatDateForAPI(formattedFilterData.toDate);

      const apiData = {
        search: {
          ...formattedFilterData,
        },
        // CR: request the filter dropdown options only once (same pattern as vacancy/companies).
        loadDropdown: !dropdownLoadedRef.current,
      };

      const response = await ApiCall.service(
        "fetch-scrapping-data",
        "POST",
        apiData,
        false,
        CENTRAL_DATA_MANAGEMENT_MICROSERVICE
      );

      if (response?.status == 200) {
        const data = response?.data;
        // CR: dropdown options arrive in the same response on first load; set once.
        if (data?.dropdownData) {
          setDropdownList((prev) => ({ ...prev, ...data.dropdownData }));
          dropdownLoadedRef.current = true;
        }
        const constructedData = data.companyData.map((data: any) => ({
          main: {
            ...data?.main,
            posted_date: data?.main?.posting_date || data?.main?.posted_date,
            posting_date: data?.main?.posting_date || data?.main?.posted_date,
          },
          basic: data?.basic,
          address: data?.address,
          contacts: data?.contacts,
          vacancies: data?.vacancies || [],
        }));

        setScrapingApplication(constructedData);
        setPaginationData({
          totalPages: data.pagination?.totalPages || 1,
          totalRecords: data.pagination?.totalRecords || 0,
        });
      } else {
        CustomNotify({
          type: "error",
          message: t("Some error occured")
        });
      }
    } catch (error) {
      console.error("Error fetching scraping data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOrderByColumn = (column: string) => {
    let newOrder: SortType = "";
    let newColumn = column;

    if (searchData.orderByColumn === column) {
      if (searchData.orderIn === "") {
        newOrder = "asc";
      } else if (searchData.orderIn === "asc") {
        newOrder = "desc";
      } else {
        newOrder = "";
        newColumn = "";
      }
    } else {
      newOrder = "asc";
    }

    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      orderByColumn: newColumn,
      orderIn: newOrder,
    }));
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const fieldMappings: Record<string, string> = {};
    const fieldName = fieldMappings[name] || name;
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      [fieldName]: value,
    }));
  };

  const handleDateChange = (date: Date | null, fieldName: string) => {
    const safeDate =
      date instanceof Date && !isNaN(date.getTime()) ? date : null;

    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      [fieldName]: safeDate,
    }));
  };

  const handlePageChange = (pageNumber: number) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: pageNumber,
    }));
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setSearchData((prev) => ({
      ...prev,
      recordsPerPage: newItemsPerPage,
      currentPage: 1,
    }));
  };

  const handleSelectChange = (selectedOption: any, fieldName: string) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      [fieldName]: selectedOption,
    }));
  };

  const handleCloseSearchModal = () => {
    setSearchModal(false);
  };

  // advert keyword: open the Advanced Search popup (holds the advert-text keyword field).
  const handleAdvSearchClick = () => {
    setSearchModal(true);
  };

  const handleSearchConfirm = () => {
    const filterData = {
      ...searchData,
      ...advSearchData,
      type: activeTab.tabFieldValue,
    };
    const searchUrl = createQueryParams({ ...filterData, ...activeTab });
    dispatch(setDestinationPath(searchUrl));
    fetchDetails(filterData);
  };

  const clearAdvSearchFilter = () => {
    setAdvSearchData(defaultAdvSearchData);
    const filterData = {
      ...searchData,
      ...defaultAdvSearchData,
      type: activeTab.tabFieldValue,
    };
    const searchUrl = createQueryParams({ ...filterData, ...activeTab });
    dispatch(setDestinationPath(searchUrl));
    fetchDetails(filterData);
  };

  const handleReset = () => {
    dispatch(clearDestinationPath());
    setSearchData({
      ...defaultSearchData,
      office: [],
      fromDate: null,
      toDate: null,
      posted_date: null,
      post_code: [] // CR: multi-select postal codes
    });
    setAdvSearchData(defaultAdvSearchData);
    setActiveTab({ tabFieldName: "type", tabFieldValue: 3 });
  };

  const handleNavItemClick = (tabFieldName: string, tabFieldValue: number) => {
    setSearchData((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    setActiveTab({ tabFieldName, tabFieldValue });
  };

  const getSortType = (columnName: string): SortType => {
    return searchData.orderByColumn === columnName
      ? (searchData.orderIn as SortType)
      : "";
  };

  return (
    <>
      <AccessControl
        requiredPermissions={[
          {
            permission: "Scraping",
            read: true,
          },
        ]}
        renderNoAccess={true}
      >
        <Title title={t("Scraping")} />

        {/* Import Status Indicator */}
        {importStatus.isImporting && (
          <div className="data-importing-wrapper text-center">
            {/* <LoadingIcon
              iconType="bars"
              className="mx-auto"
              width={"2vw"}
              height={"2vw"}
              
            /> */}
            <div>
              <span>{t("Data import in progress")}</span>
              <br />
              <span className="marginRightPoint5">
                {t("Importing file")}: {importStatus.fileName}
              </span>
              <span className="spinner-border spinnerIcon "></span>
            </div>
          </div>
        )}

        <div className="search-bar">
          <div className="row">
            <div className="col-md-12 company-tabs">
              <NavBar
                navItems={navItems}
                onNavItemClick={handleNavItemClick}
                className="custom-navbar"
                activeItem={activeTab}
              />
            </div>
          </div>
        </div>

        <div className="position-relative tableMainWrapper">
          <div className="row">
            <div className="ManageCreateBtn">
              <AccessControl
                requiredPermissions={[
                  {
                    permission: "Scraping",
                    create: true,
                  },
                ]}
              >
                <LinkTo
                  pagelink="/scraping"
                  title={"+ " + t("Add scraping")}
                  className="marginRightPoint5"
                />
              </AccessControl>
              <AccessControl
                requiredPermissions={[
                  {
                    permission: "Export Files",
                    read: true,
                  },
                ]}
              >
                <ExportFile
                  source="scraping"
                  searchParams={{ ...searchData, type: activeTab.tabFieldValue }}
                  microService={CENTRAL_DATA_MANAGEMENT_MICROSERVICE}
                  className="marginRightPoint5"
                />
              </AccessControl>
              
            </div>
          </div>
          <div className="table-responsive tableSection">
            <table className="table manageScrapping border-0 mb-0">
              <thead>
                <tr className="tableSearchFields">
                  <SearchFields
                    searchData={searchData}
                    filters={filters}
                    handleSearchChange={handleSearchChange}
                    handleSelectChange={handleSelectChange}
                    handleDateChange={handleDateChange}
                  />
                  <th className="border-0">
                    <div className="d-flex align-items-center w-100">
                      <ResetBtn handleResetClick={handleReset} />
                      {/* advert keyword: opens Advanced Search (advert-text keyword). */}
                      <AdvancedSearchBtn
                        handleSearch={handleAdvSearchClick}
                        clearAdvSearchFilter={clearAdvSearchFilter}
                        filterData={advSearchData}
                      />
                    </div>
                  </th>
                </tr>
              </thead>
            </table>

            <div className="tableContainer manageScrapping scrollBarDesign">
              <table className="table table-hover">
                <thead>
                  <tr className="TableHeader">
                    <th onClick={() => handleOrderByColumn("posting_date")}>
                      <div className="d-flex align-items-center">
                        <TextEllipsis
                          title={t("Posted Date")}
                          label={t("Posted Date")}
                          width="4.25vw"
                        />
                        <Sorting sortType={getSortType("posting_date")} />
                      </div>
                    </th>
                    <th onClick={() => handleOrderByColumn("position")}>
                      {t("Position")}
                      <Sorting sortType={getSortType("position")} />
                    </th>

                    <th onClick={() => handleOrderByColumn("company")}>
                      {t("Company")}
                      <Sorting sortType={getSortType("company")} />
                    </th>

                    <th onClick={() => handleOrderByColumn("post_code")}>
                      <div className="d-flex align-items-center">
                        <TextEllipsis
                          title={t("Postal Code")}
                          label={t("Postal Code")}
                          width="3vw"
                        />
                        <Sorting sortType={getSortType("post_code")} />
                      </div>
                    </th>

                    <th onClick={() => handleOrderByColumn("location")}>
                      {t("Location")}
                      <Sorting sortType={getSortType("location")} />
                    </th>
                    <th onClick={() => handleOrderByColumn("vat")}>
                      {t("VAT number")}
                      <Sorting sortType={getSortType("vat")} />
                    </th>
                    <th onClick={() => handleOrderByColumn("Contact Person")}>
                      <div className="d-flex align-items-center">
                        <TextEllipsis
                          title={t("Contact person")}
                          label={t("Contact person")}
                          width="5vw"
                        />
                        <Sorting sortType={getSortType("Contact Person")} />
                      </div>
                    </th>

                    <th onClick={() => handleOrderByColumn("phoneNumber")}>
                      {t("Phone no.")}
                      <Sorting sortType={getSortType("phoneNumber")} />
                    </th>
                    <th className="align-middle">{t("Email")}</th>
                    <th className="align-middle">{t("Source")}</th>
                    <th onClick={() => handleOrderByColumn("created_at")}>
                      {t("Created at")}
                      <Sorting sortType={getSortType("created_at")} />
                    </th>
                    <th className="align-middle">{t("Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <SpinnerWrapper headerLength={12} />
                  ) : (
                    <>
                      {ScrapingApplication && ScrapingApplication.length > 0 ? (
                        ScrapingApplication.map((ScrapingApplication) => (
                          <tr
                            key={ScrapingApplication.main.id}
                            className="border-bottom mb-3 box-shadow align-middle"
                          >
                            <td
                              data-label={t("Posted Date")}
                              className="align-middle"
                            >
                              {formatDate(
                                ScrapingApplication?.main?.posting_date ||
                                ScrapingApplication?.main?.posted_date
                              )}
                            </td>
                            <td
                              data-label={t("position")}
                              className="align-middle text-break"
                            >
                              {/* CR-581 (Req 1): Position is now a click-through link to the
                                  scraped position detail view (same target as the Proceed arrow). */}
                              <Link
                                to={`/view-scrap/${ScrapingApplication.main.id}`}
                              >
                                <TextEllipsis
                                  title={
                                    ScrapingApplication?.basic?.position !== " - "
                                      ? ScrapingApplication?.basic?.position
                                      : ""
                                  }
                                  label={
                                    ScrapingApplication?.basic?.position !== " - "
                                      ? ScrapingApplication?.basic?.position
                                      : ""
                                  }
                                  width="6vw"
                                />
                              </Link>
                            </td>
                            <td
                              data-label={t("company")}
                              className="align-middle text-break"
                            >
                              <TextEllipsis
                                title={ScrapingApplication?.basic?.company}
                                label={ScrapingApplication?.basic?.company}
                                width="6vw"
                              />
                            </td>
                            <td
                              data-label={t("Postal Code")}
                              className="align-middle text-break"
                            >
                              {ScrapingApplication?.address?.post_code}
                            </td>
                            <td
                              className="text-break"
                              data-label={t("Location")}
                            >
                              <TextEllipsis
                                title={ScrapingApplication?.address?.city1}
                                label={ScrapingApplication?.address?.city1}
                                width="6vw"
                              />
                            </td>
                            <td
                              data-label={t("VAT number")}
                              className="align-middle text-break"
                            >
                              <TextEllipsis
                                title={ScrapingApplication?.basic?.vat}
                                label={ScrapingApplication?.basic?.vat}
                                width="6vw"
                              />
                            </td>
                            <td
                              data-label={t("Contact Person")}
                              className="align-middle text-break"
                            >
                              {ScrapingApplication?.contacts &&
                                ScrapingApplication.contacts.length > 0 &&
                                (ScrapingApplication.contacts[0]?.fName ||
                                  ScrapingApplication.contacts[0]?.lName) && (
                                  <TextEllipsis
                                    title={`${ScrapingApplication.contacts[0]?.fName ||
                                      ""
                                      } ${ScrapingApplication.contacts[0]?.lName ||
                                      ""
                                      }`.trim()}
                                    label={`${ScrapingApplication.contacts[0]?.fName ||
                                      ""
                                      } ${ScrapingApplication.contacts[0]?.lName ||
                                      ""
                                      }`.trim()}
                                    width="6vw"
                                  />
                                )}
                            </td>

                            <td className="text-break" data-label={t("phone")}>
                              {/* Phone number as text with ClaritalkCallIcon → 3CX */}
                              {ScrapingApplication?.basic?.telephone && (
                                <ClaritalkCallIcon
                                  entityId={null}
                                  entityType="company"
                                  entityName={
                                    ScrapingApplication?.basic?.company ?? ""
                                  }
                                  phone={ScrapingApplication.basic.telephone}
                                  showPhoneIcon={true}
                                  showAsText={true}
                                  navigateTo3CX={true}
                                />
                              )}
                            </td>
                            <td
                              data-label={t("Email")}
                              className="align-middle text-break"
                            >
                              {ScrapingApplication?.contacts &&
                                ScrapingApplication.contacts.length > 0 &&
                                ScrapingApplication.contacts[0]?.email && (
                                  <a
                                    href={`mailto:${ScrapingApplication.contacts[0].email}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <TextEllipsis
                                      title={
                                        ScrapingApplication.contacts[0].email
                                      }
                                      label={
                                        ScrapingApplication.contacts[0].email
                                      }
                                      width="11vw"
                                    />
                                  </a>
                                )}
                            </td>
                            <td
                              data-label={t("Source")}
                              className="align-middle text-break"
                            >
                              <TextEllipsis
                                title={ScrapingApplication?.basic?.source}
                                label={ScrapingApplication?.basic?.source}
                                width="6vw"
                              />
                            </td>
                            <td
                              data-label={t("Created at")}
                              className="align-middle"
                            >
                              {formatDate(
                                ScrapingApplication?.main?.created_at
                              )}
                            </td>
                            <td className="table-action-icons px-2 align-middle">
                              <Link
                                to={`/view-scrap/${ScrapingApplication.main.id}`}
                                className="marginRightPoint5"
                              >
                                <span
                                  data-bs-toggle="tooltip"
                                  title={t("Proceed")}
                                >
                                  <RightArrow width={"2vw"} height={"2vw"} />
                                </span>
                              </Link>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <NoRecords headerLength={12} />
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {!loading && (
              <div className="pagination d-block">
                <PaginationNew
                  currentPage={searchData.currentPage}
                  totalPages={paginationData.totalPages}
                  totalRecords={paginationData.totalRecords}
                  recordsPerPage={searchData.recordsPerPage}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  onPageChange={(newPage: number) => handlePageChange(newPage)}
                />
              </div>
            )}
          </div>

          <div className="row backPadding">
            <div className="col-md-6 align-self-center">
              <Link
                to={"/dashboard"}
                className="back-btn text-decoration-underline"
              >
                {t("Back")}
              </Link>
            </div>
          </div>

          <AdvancedSearch
            show={searchModal}
            onHide={handleCloseSearchModal}
            onSearchConfirm={handleSearchConfirm}
            advSearchData={advSearchData}
            setAdvSearchData={setAdvSearchData}
            fieldConfigs={filters}
            clearAdvSearchFilter={clearAdvSearchFilter}
          />
        </div>
      </AccessControl>
    </>
  );
};

export default ManageScraping;
